import { apiClient } from '@/utils/api-client';
import type { ApiResponse, JsonApiResource } from '@/types/api';

export interface EvalCase {
  id: string;
  name: string;
  query: string;
  expected_response: string | null;
  expected_domains: string[];
  task_type: string | null;
  source_type: string;
  metadata: Record<string, unknown>;
  is_active: boolean;
  skill_template_id: string | null;
  skill_template_name: string | null;
  created_at: string;
  updated_at: string;
}

// Which account's entitlements scoped a run's retrieval. Recorded into
// `configuration` when the run starts; absent on runs from before it was added.
export interface RanAs {
  user_id: number;
  email: string | null;
  account_id: number | null;
  account_name: string | null;
}

export interface EvalRun {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  total_cases: number;
  passed_cases: number;
  failed_cases: number;
  overall_score: number | null;
  configuration: Record<string, unknown> & { ran_as?: RanAs | null };
  summary_stats: Record<string, unknown>;
  created_by: string | null;
  skill_template_id: string | null;
  skill_template_name: string | null;
  scoring_method: string | null;
  started_at: string | null;
  completed_at: string | null;
  results?: EvalResult[];
  created_at: string;
  updated_at: string;
}

export interface EvalResult {
  id: string;
  eval_case_id: string;
  eval_case_name: string | null;
  status: 'pending' | 'passed' | 'failed' | 'error';
  score: number | null;
  latency_ms: number | null;
  scoring_details: Record<string, unknown>;
}

export interface EvalCaseCreateData {
  name: string;
  query: string;
  expected_response?: string;
  expected_domains?: string[];
  task_type?: string;
  source_type?: string;
  is_active?: boolean;
  skill_template_id?: string;
  metadata?: Record<string, unknown>;
}

export interface ToolingUser {
  user_id: string;
  email: string;
  account_id: string;
  account_name: string | null;
}

export interface TaggableProject {
  id: string;
  name: string;
  status: string | null;
}

export const PROJECT_CONTEXT_TASK_TYPE = 'project_context';

export interface ProjectCaseMetadata {
  project_id?: string;
  expected_fields?: string[];
}

export const projectCaseMetadata = (evalCase: { metadata?: Record<string, unknown> }): ProjectCaseMetadata => {
  const meta = (evalCase.metadata || {}) as ProjectCaseMetadata;
  return { project_id: meta.project_id, expected_fields: meta.expected_fields };
};

export interface EvalCaseStats {
  total: number;
  active: number;
  by_source: Record<string, number>;
  task_types: string[];
}

export const evalService = {
  // Cases
  async listCases(params?: { active?: string; task_type?: string }): Promise<ApiResponse<JsonApiResource<EvalCase>[]>> {
    const queryParams: Record<string, string> = {};
    if (params?.active) queryParams.active = params.active;
    if (params?.task_type) queryParams.task_type = params.task_type;
    return apiClient.get('/api/admin/eval_cases', queryParams);
  },

  async getCase(id: string): Promise<ApiResponse<JsonApiResource<EvalCase>>> {
    return apiClient.get(`/api/admin/eval_cases/${id}`);
  },

  async createCase(data: EvalCaseCreateData): Promise<ApiResponse<JsonApiResource<EvalCase>>> {
    return apiClient.post('/api/admin/eval_cases', { eval_case: data });
  },

  async updateCase(id: string, data: Partial<EvalCaseCreateData>): Promise<ApiResponse<JsonApiResource<EvalCase>>> {
    return apiClient.patch(`/api/admin/eval_cases/${id}`, { eval_case: data });
  },

  async destroyCase(id: string): Promise<void> {
    return apiClient.delete(`/api/admin/eval_cases/${id}`);
  },

  async importCases(cases: EvalCaseCreateData[]): Promise<ApiResponse<JsonApiResource<EvalCase>[]>> {
    return apiClient.post('/api/admin/eval_cases/import', { cases });
  },

  async getCaseStats(): Promise<EvalCaseStats> {
    return apiClient.get('/api/admin/eval_cases/stats');
  },

  // Runs
  async listRuns(params?: { status?: string }): Promise<ApiResponse<JsonApiResource<EvalRun>[]>> {
    const queryParams: Record<string, string> = {};
    if (params?.status) queryParams.status = params.status;
    return apiClient.get('/api/admin/eval_runs', queryParams);
  },

  async getRun(id: string): Promise<ApiResponse<JsonApiResource<EvalRun>>> {
    return apiClient.get(`/api/admin/eval_runs/${id}`);
  },

  async createRun(data: { name: string; skill_template_id?: string; configuration?: Record<string, unknown> }): Promise<ApiResponse<JsonApiResource<EvalRun>>> {
    return apiClient.post('/api/admin/eval_runs', { eval_run: data });
  },

  // Project context
  async listTaggableProjects(): Promise<{ projects: TaggableProject[]; error?: string; tooling_user?: ToolingUser }> {
    return apiClient.get('/api/admin/eval_project_context/projects');
  },

  async listProjectLabels(projectId: string): Promise<{ labels: string[] }> {
    return apiClient.get('/api/admin/eval_project_context/labels', { project_id: projectId });
  },
};

